import { useCallback, useState } from 'react';
import { Button, Modal } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import type { RidesUploadLinkView } from '@actschurch/ccms-sdk';
import { errorMessage } from '@/lib/copy';
import { isPdfType } from '@/lib/documents';

interface OpenImage {
  url: string;
  title: string;
  minutes: number;
}

export interface DocumentViewer {
  /** Fetch a fresh short-lived link and show it: photos in a modal, PDFs in a new tab. */
  open: (rideUploadId: number, title: string, contentType: string | null | undefined) => Promise<void>;
  /** The upload whose link is being fetched (spinner on its View button). */
  loadingId: number | null;
  error: string | null;
  clearError: () => void;
  /** Render once inside the component using the hook. */
  modal: JSX.Element;
}

/**
 * Opens private documents through a link the server signs for a few minutes. Links are never
 * cached: each tap asks for a new one and closing the modal drops it.
 * For PDFs the tab is opened inside the tap (popup blockers allow nothing later) and pointed at the
 * link once it arrives.
 */
export function useDocumentViewer(getLink: (rideUploadId: number) => Promise<RidesUploadLinkView>): DocumentViewer {
  const [image, setImage] = useState<OpenImage | null>(null);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fullScreen = useMediaQuery('(max-width: 480px)') ?? false;

  const open = useCallback(
    async (rideUploadId: number, title: string, contentType: string | null | undefined) => {
      setError(null);
      const expectPdf = isPdfType(contentType);
      let win: Window | null = null;
      if (expectPdf) {
        win = window.open('', '_blank');
        if (win) {
          try {
            win.opener = null;
            win.document.title = 'Opening document…';
            win.document.body.textContent = 'Opening your document…';
          } catch {
            /* cross-origin about:blank quirks; the navigation below still works */
          }
        }
      }
      setLoadingId(rideUploadId);
      try {
        const link = await getLink(rideUploadId);
        if (isPdfType(link.contentType) || expectPdf) {
          if (win) win.location.replace(link.url);
          else window.open(link.url, '_blank', 'noopener,noreferrer');
        } else {
          win?.close();
          setImage({ url: link.url, title, minutes: Math.max(1, Math.round((link.expiresInSeconds || 300) / 60)) });
        }
      } catch (e) {
        win?.close();
        setError(errorMessage(e, "We couldn't open that document. Please try again."));
      } finally {
        setLoadingId(null);
      }
    },
    [getLink],
  );

  const modal = (
    <Modal opened={!!image} onClose={() => setImage(null)} title={image?.title ?? 'Document'} size="lg" centered fullScreen={fullScreen}>
      {image && (
        <>
          <img
            src={image.url}
            alt={`Uploaded document: ${image.title}`}
            referrerPolicy="no-referrer"
            draggable={false}
            style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 10, background: 'var(--rides-surface-2)' }}
          />
          <p style={{ margin: '12px 0 0', color: 'var(--rides-muted)', fontSize: 15, lineHeight: 1.45 }}>
            This private link stops working in about {image.minutes} minute{image.minutes === 1 ? '' : 's'}. Please close it when you're done.
          </p>
          <Button fullWidth variant="default" mt="sm" onClick={() => setImage(null)}>Close</Button>
        </>
      )}
    </Modal>
  );

  return { open, loadingId, error, clearError: useCallback(() => setError(null), []), modal };
}
